import React, { useEffect } from "react";
import { Select, Typography, Form, theme, Divider, Input, Flex } from "antd";
import { useTranslation } from "react-i18next";
import { GET_PAID_STATUS, GET_PAID_STEP, GetPaidData, GetPaidRawFormData } from "types/get_paid";
import { useStepValidation, useGetPaidMixpanelEvent } from "hooks/get_paid";
import { prefillGetPaidForm } from "helpers/get_paid";

const { Text, Title } = Typography;

const AutomationStep: React.FC<{
  getPaidInitialData?: GetPaidData | null;
  getPaidStatus?: GET_PAID_STATUS;
  handleShouldGoNext: (shouldGoNext: boolean) => void;
}> = ({ getPaidInitialData, getPaidStatus, handleShouldGoNext }) => {
  const { t, i18n } = useTranslation();
  const { token } = theme.useToken();
  const form = Form.useFormInstance<GetPaidRawFormData>();

  useGetPaidMixpanelEvent({
    eventName: "AR_GP_Step_2",
    fieldGroup: GET_PAID_STEP.AUTOMATION,
  });

  useStepValidation({ handleShouldGoNext });

  useEffect(() => {
    if (getPaidInitialData && getPaidStatus === GET_PAID_STATUS.REJECTED) {
      prefillGetPaidForm(
        form,
        GET_PAID_STEP.AUTOMATION,
        [
          "iban_account",
          "qpay_account",
          "bank_transfer_fee_account",
          "bank_acc_holder_name",
          "bank_id",
          "iban_number",
        ],
        getPaidInitialData,
      );
    }
  }, [getPaidStatus]);

  return (
    <>
      <Title level={4} style={{ marginBottom: 0, fontWeight: token.fontWeightStrong }}>
        {t("templates.integrations.getpaid_activation.automation_setup.header")}
      </Title>
      <Text type="secondary">
        {t("templates.integrations.getpaid_activation.automation_setup.subtitle")}
      </Text>
      <Divider />
      <Title level={5} style={{ fontWeight: token.fontWeightStrong }}>
        {t("templates.integrations.getpaid_activation.automation_setup.pay_in_automation")}
      </Title>
      <Flex gap={token.marginXXS} vertical style={{ marginBottom: token.marginSM }}>
        <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
          {t(
            "templates.integrations.getpaid_activation.automation_setup.pay_in_automation_subtitle",
          )}
        </Typography.Paragraph>
        {i18n.language === "ar" && (
          <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
            {t(
              "templates.integrations.getpaid_activation.automation_setup.pay_in_automation_subtitle_extra_note",
            )}
          </Typography.Paragraph>
        )}
      </Flex>
      <Form.Item
        name={[GET_PAID_STEP.AUTOMATION, "qpay_account"]}
        label={t(
          "templates.integrations.getpaid_activation.automation_setup.track_collected_funds_in",
        )}
        rules={[{ required: true }]}
        extra={t(
          "templates.integrations.getpaid_activation.automation_setup.track_collected_funds_in_note",
        )}
      >
        <Select
          size="large"
          options={getPaidInitialData?.qpay_account?.values}
          placeholder={t(
            "templates.integrations.getpaid_activation.automation_setup.track_collected_funds_in_placeholder",
          )}
        />
      </Form.Item>
      <Form.Item
        name={[GET_PAID_STEP.AUTOMATION, "bank_transfer_fee_account"]}
        label={t(
          "templates.integrations.getpaid_activation.automation_setup.track_tahseel_fees_in",
        )}
        rules={[{ required: true }]}
        extra={t(
          "templates.integrations.getpaid_activation.automation_setup.track_tahseel_fees_in_note",
        )}
      >
        <Select
          size="large"
          options={getPaidInitialData?.bank_transfer_fee_account?.values}
          placeholder={t(
            "templates.integrations.getpaid_activation.automation_setup.track_tahseel_fees_in_placeholder",
          )}
        />
      </Form.Item>
      <Divider />
      <Title level={5} style={{ fontWeight: token.fontWeightStrong }}>
        {t("templates.integrations.getpaid_activation.automation_setup.pay_out_automation")}
      </Title>
      <Typography.Paragraph type="secondary" style={{ marginBottom: token.marginSM }}>
        {t(
          "templates.integrations.getpaid_activation.automation_setup.pay_out_automation_subtitle",
        )}
      </Typography.Paragraph>
      <Form.Item
        name={[GET_PAID_STEP.AUTOMATION, "bank_id"]}
        label={t("templates.integrations.getpaid_activation.automation_setup.bank_name")}
        rules={[{ required: true }]}
        extra={t("templates.integrations.getpaid_activation.automation_setup.bank_name_note")}
      >
        <Select
          size="large"
          options={getPaidInitialData?.bank_id?.values}
          placeholder={t(
            "templates.integrations.getpaid_activation.automation_setup.bank_name_placeholder",
          )}
        />
      </Form.Item>
      <Form.Item
        name={[GET_PAID_STEP.AUTOMATION, "bank_acc_holder_name"]}
        label={t("templates.integrations.getpaid_activation.automation_setup.bank_holder_name")}
        rules={[
          { required: true },
          { whitespace: true },
          {
            pattern: /^[A-Za-z\s]+$/,
            message: t(
              "templates.integrations.getpaid_activation.automation_setup.bank_account_name_validation_msg",
            ),
          },
        ]}
        extra={t(
          "templates.integrations.getpaid_activation.automation_setup.bank_account_name_note",
        )}
      >
        <Input
          size="large"
          placeholder={t(
            "templates.integrations.getpaid_activation.automation_setup.bank_account_name_placeholder",
          )}
        />
      </Form.Item>
      <Form.Item
        name={[GET_PAID_STEP.AUTOMATION, "iban_number"]}
        label={t("templates.integrations.getpaid_activation.automation_setup.bank_account_iban")}
        rules={[
          { required: true },
          {
            pattern: /^SA\d{22}$/,
            message: t(
              "templates.integrations.getpaid_activation.automation_setup.bank_account_iban_invalid",
            ),
          },
        ]}
        extra={t(
          "templates.integrations.getpaid_activation.automation_setup.bank_account_iban_note",
        )}
      >
        <Input
          size="large"
          placeholder={t(
            "templates.integrations.getpaid_activation.automation_setup.bank_account_iban_placeholder",
          )}
        />
      </Form.Item>
    </>
  );
};

export default AutomationStep;
